import React, { useState } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useNavigate } from 'react-router-dom';
import { IS_APP_DOMAIN } from './lib/domain';
import { Menu, Command } from 'lucide-react';
import Sidebar from './components/shared/Sidebar';
import TalentPoolView from './components/talent-pool/TalentPoolView';
import MissionRegistryView from './components/mission-registry/MissionRegistryView';
import ClientsView from './components/clients/ClientsView';
import MatchingEngineView from './components/matching-engine/MatchingEngineView';
import HomeDashboardView from './components/pages/HomeDashboardView';
import SettingsView from './components/pages/SettingsView';
import PublicCandidateView from './components/pages/PublicCandidateView';
import LoginPage from './components/pages/LoginPage';
import SignupPage from './components/pages/SignupPage';
import InvitePage from './components/pages/InvitePage';
import AuthGuard from './components/shared/AuthGuard';
import LandingPage from './components/pages/LandingPage';
import { LANDING_SECTIONS } from './components/pages/landingSections';
import OnboardingPage from './components/pages/OnboardingPage';
import AuthCallbackPage from './components/pages/AuthCallbackPage';
import ForgotPasswordPage from './components/pages/ForgotPasswordPage';
import UpdatePasswordPage from './components/pages/UpdatePasswordPage';
import PrivacyPage from './components/pages/PrivacyPage';
import CGUPage from './components/pages/CGUPage';
import { Analytics } from '@vercel/analytics/react';
import FeedbackWidget from './components/feedback/FeedbackWidget';
import type { AppView } from './types';
import { ProcessingProvider } from './context/ProcessingContext';
import { NotificationProvider } from './context/NotificationContext';
import { MatchingProvider } from './context/MatchingContext';
import { AuthProvider } from './context/AuthContext';
import { Toaster } from 'sonner';

// Inner layout — must be inside BrowserRouter to use useNavigate
function AppLayout() {
  const navigate = useNavigate();
  const [selectedCandidate, setSelectedCandidate] = useState<any>(null);
  const [sidebarOpen, setSidebarOpen] = useState(false);

  // Map legacy AppView values to URL paths (used by HomeDashboard + TalentPool)
  const handleChangeView = (view: AppView) => {
    const paths: Record<AppView, string> = {
      'home': '/home',
      'talent-pool': '/talents',
      'missions-registry': '/missions',
      'smart-matching': '/matching',
    };
    navigate(paths[view] ?? '/');
  };

  return (
    <ProcessingProvider onNavigateToCandidate={(id) => navigate(`/talents/${id}`)} onNavigateToMission={(id) => navigate(`/missions/${id}`)}>
      <MatchingProvider>
        <div className="min-h-screen bg-slate-50 flex">
          {/* Topbar mobile — masquée sur desktop */}
          <header className="md:hidden fixed top-0 left-0 right-0 z-50 h-14 bg-slate-950 border-b border-slate-800 flex items-center justify-between px-4 shadow-md">
            <div className="flex items-center gap-2">
              <div className="bg-amber-600 p-1.5 rounded-lg">
                <Command size={16} className="text-white" />
              </div>
              <span className="font-bold text-base tracking-tight text-white">
                Revert<span className="text-amber-500">AI</span>
              </span>
            </div>
            <button
              onClick={() => setSidebarOpen(true)}
              className="p-2 rounded-lg text-slate-400 hover:text-white hover:bg-slate-800 transition-colors"
              aria-label="Ouvrir le menu"
            >
              <Menu size={20} />
            </button>
          </header>

          <Sidebar isOpen={sidebarOpen} onClose={() => setSidebarOpen(false)} />
          <main className="flex-1 md:ml-64 pt-14 md:pt-0 px-4 py-4 md:px-8 md:pb-8 md:pt-10 overflow-y-auto h-screen">
            <div className="max-w-[95%] mx-auto">
              <Routes>
                <Route index element={<HomeDashboardView />} />
                <Route path="home" element={<HomeDashboardView />} />
                <Route path="missions/:id?/:tab?" element={<MissionRegistryView />} />
                <Route path="clients/:id?" element={<ClientsView />} />
                <Route
                  path="talents/:id?"
                  element={
                    <TalentPoolView
                      onChangeView={handleChangeView}
                      onSelectCandidate={setSelectedCandidate}
                    />
                  }
                />
                <Route
                  path="matching/:taskId?"
                  element={
                    <MatchingEngineView
                      preSelectedCandidate={selectedCandidate}
                      onConsumePreSelected={() => setSelectedCandidate(null)}
                    />
                  }
                />
                <Route path="settings" element={<SettingsView />} />
                <Route path="*" element={<Navigate to="/home" replace />} />
              </Routes>
            </div>
          </main>
          <Toaster richColors position="top-right" />
          <FeedbackWidget />
        </div>
      </MatchingProvider>
    </ProcessingProvider>
  );
}

function App() {
  // Public share view: keep query-param detection before BrowserRouter
  const searchParams = new URLSearchParams(window.location.search);
  if (searchParams.has('token')) {
    return (
      <AuthProvider>
        <PublicCandidateView />
      </AuthProvider>
    );
  }

  return (
    <BrowserRouter>
      <AuthProvider>
        <NotificationProvider>
          <Routes>
            {IS_APP_DOMAIN ? (
              // app.revertai.io — routes protégées + onboarding
              // En dev/preview (IS_DEV), toutes les routes sont exposées sur le même domaine
              <>
                <Route path="/" element={<LandingPage />} />
                {/* Sections de la landing : même page, position de défilement différente. */}
                {LANDING_SECTIONS.map(s => (
                  <Route key={s.path} path={s.path} element={<LandingPage />} />
                ))}
                <Route path="/login" element={<LoginPage />} />
                <Route path="/signup" element={<SignupPage />} />
                <Route path="/forgot-password" element={<ForgotPasswordPage />} />
                <Route path="/update-password" element={<UpdatePasswordPage />} />
                <Route path="/invite/:token" element={<InvitePage />} />
                <Route path="/privacy" element={<PrivacyPage />} />
                <Route path="/cgu" element={<CGUPage />} />
                <Route path="/onboarding" element={<OnboardingPage />} />
                <Route path="/auth/callback" element={<AuthCallbackPage />} />
                <Route
                  path="/*"
                  element={
                    <AuthGuard>
                      <AppLayout />
                    </AuthGuard>
                  }
                />
              </>
            ) : (
              // revertai.io — landing + auth publique uniquement
              <>
                <Route path="/" element={<LandingPage />} />
                {/* Sections de la landing : même page, position de défilement différente. */}
                {LANDING_SECTIONS.map(s => (
                  <Route key={s.path} path={s.path} element={<LandingPage />} />
                ))}
                <Route path="/login" element={<LoginPage />} />
                <Route path="/signup" element={<SignupPage />} />
                <Route path="/forgot-password" element={<ForgotPasswordPage />} />
                <Route path="/update-password" element={<UpdatePasswordPage />} />
                <Route path="/invite/:token" element={<InvitePage />} />
                <Route path="/privacy" element={<PrivacyPage />} />
                <Route path="/cgu" element={<CGUPage />} />
                <Route path="*" element={<Navigate to="/" replace />} />
              </>
            )}
          </Routes>
        </NotificationProvider>
      </AuthProvider>
      <Analytics />
    </BrowserRouter>
  );
}

export default App;
